import { Stack, Text, Title } from "@mantine/core";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const EMBED_CONTAINER_ID = "therapprove-embed";
const TEST_EMBED_TITLE = "Therapprove Test Form";

const removeTrailingSlash = (value: string) => value.replace(/\/$/, "");

export const TestClientEmbed = () => {
    const [searchParams] = useSearchParams();

    const landingUrl = removeTrailingSlash(window.location.origin);
    const queryOrganizationId = searchParams.get("organizationId");
    const organizationId = queryOrganizationId || "";
    const successUrl = searchParams.get("success_url") || "/";

    const dataSrc = useMemo(() => {
        if (!organizationId) return "";
        return `${landingUrl}/organizations/${organizationId}/embedded-priority-view-form`;
    }, [landingUrl, organizationId]);

    const embedCode = useMemo(() => {
        if (!dataSrc) return "";
        return `<div id="${EMBED_CONTAINER_ID}"></div>
      <script
        src="${landingUrl}/embed.js"
        data-src="${dataSrc}"
        data-container="#${EMBED_CONTAINER_ID}"
        data-height="auto"
        data-title="${TEST_EMBED_TITLE}"
        data-success-url="${successUrl}"
        async
      ></script>`;
    }, [dataSrc, landingUrl, successUrl]);

    useEffect(() => {
        if (!embedCode) return;

        const container = document.getElementById(EMBED_CONTAINER_ID);
        if (!container) return;

        container.innerHTML = "";

        const script = document.createElement("script");
        script.src = `${landingUrl}/embed.js`;
        script.async = true;
        script.dataset.src = dataSrc;
        script.dataset.container = `#${EMBED_CONTAINER_ID}`;
        script.dataset.height = "auto";
        script.dataset.title = TEST_EMBED_TITLE;
        script.dataset.successUrl = successUrl;

        container.parentElement?.appendChild(script);

        return () => {
            script.remove();
            container.innerHTML = "";
        };
    }, [embedCode, landingUrl, dataSrc, successUrl]);

    if (!organizationId) {
        return (
            <Stack p="xl" maw={900} mx="auto" gap="md">
                <Title order={2}>Test Client Embed Page</Title>
                <Text>
                    Missing organization id. Pass `?organizationId=...` in the
                    URL.
                </Text>
            </Stack>
        );
    }

    return (
        <Stack p="xl" maw={900} mx="auto" gap="md">
            <Title order={2}>Test Client Embed Page</Title>
            <Text>
                This page simulates a client website that pasted the Therapprove
                embed snippet.
            </Text>
            <div
                id={EMBED_CONTAINER_ID}
                data-testid="test.client.embed.container"
                style={{
                    border: "1px solid #ccc",
                    borderRadius: "0.65rem",
                    padding: "1rem"
                }}
            />
        </Stack>
    );
};
